import React, { useEffect, useState } from 'react';
import { CustomTextField, FullAddressForm } from 'components';
import { Box } from '@mui/material';
import ImageUpload from 'components/ImageUpload/ImageUpload';
import { texts } from '../../utils/languages/fr';
import PropTypes from 'prop-types';

const SGPCompanyForm = ({
    form,
    handleChange,
    handleAddressChange,
    errors,
    handleLogoChange,
    handleRemoveLogo,
    handleSignatureChange,
    handleRemoveSignature,
}) => {
    const [selectedLogoFile, setSelectedLogoFile] = useState(null);
    const [selectedSignatureFile, setSelectedSignatureFile] = useState(null);

    useEffect(() => {
        if (form.logoUrl) {
            fetch(form.logoUrl)
                .then(response => response.blob())
                .then(blob => {
                    const file = new File([blob], 'logo.png', { type: blob.type });
                    setSelectedLogoFile(file);
                });
        }
    }, [form.logoUrl]);

    useEffect(() => {
        if (form.signatureImageUrl) {
            fetch(form.signatureImageUrl)
                .then(response => response.blob())
                .then(blob => {
                    const file = new File([blob], 'signature.png', { type: blob.type });
                    setSelectedSignatureFile(file);
                });
        }
    }, [form.signatureImageUrl]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <CustomTextField
                fullWidth
                label={texts.SGP_PROVIDER_NAME}
                value={form.name}
                onChange={handleChange('name')}
                error={errors.name}
            />
            <CustomTextField
                fullWidth
                label={texts.RCS_NUMBER}
                value={form.rcsNumber}
                onChange={handleChange('rcsNumber')}
                error={errors.rcsNumber}
            />
            <CustomTextField
                fullWidth
                label={texts.RCS_ADDRESS}
                value={form.rcsRegistrationAddress}
                onChange={handleChange('rcsRegistrationAddress')}
                error={errors.rcsRegistrationAddress}
            />
            <CustomTextField
                fullWidth
                label={texts.AMF_NUMBER}
                value={form.amfData}
                onChange={handleChange('amfData')}
                error={errors.amfData}
            />
            <CustomTextField
                fullWidth
                label={texts.LEGAL_REPRESENTATIVE_LAST_NAME}
                value={form.legalRepresentativeName}
                onChange={handleChange('legalRepresentativeName')}
                error={errors.legalRepresentativeName}
            />
            <CustomTextField
                fullWidth
                label={texts.LEGAL_REPRESENTATIVE_POSITION}
                value={form.legalRepresentativeFunction}
                onChange={handleChange('legalRepresentativeFunction')}
                error={errors.legalRepresentativeFunction}
            />
            <CustomTextField
                fullWidth
                label={texts.CONTACT_EMAIL}
                value={form.contactEmail}
                onChange={handleChange('contactEmail')}
                error={errors.contactEmail}
            />
            <CustomTextField
                fullWidth
                label={texts.SGP_DISTRIBUTOR_BILLING_EMAIL}
                value={form.distributorBillEmail}
                onChange={handleChange('distributorBillEmail')}
                error={errors.distributorBillEmail}
            />
            <CustomTextField
                fullWidth
                label={texts.BANK_PROVIDER_IDENTIFIER}
                value={form.bankIdentifier}
                onChange={handleChange('bankIdentifier')}
                error={errors.bankIdentifier}
            />
            <CustomTextField
                fullWidth
                multiline
                rows={4}
                label={texts.DOCUMENTS_FOOTER_TEXT}
                value={form.documentsFooterText}
                onChange={handleChange('documentsFooterText')}
                error={errors.documentsFooterText}
            />
            <FullAddressForm
                styles={{ width: '100%' }}
                noFixedWidth
                label={texts.ADRESSE_DU_SIEGE_SOCIAL}
                address={form.currentAddress}
                onAddressChange={handleAddressChange}
                errors={errors}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '20px',
                    width: '100%',
                }}
            >
                <ImageUpload
                    type="image"
                    id="sgp-logo"
                    width="100%"
                    label={texts.SGP_PROVIDER_LOGO}
                    ContainerProps={{
                        width: '100%',
                        marginTop: 0,
                        marginBottom: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    selectedFiles={
                        selectedLogoFile ? [selectedLogoFile] : form.logo ? [form.logo] : []
                    }
                    handleFileChange={handleLogoChange}
                    removeFile={() => {
                        handleRemoveLogo();
                        setSelectedLogoFile(null);
                    }}
                    error={errors.logo}
                />
                <ImageUpload
                    type="image"
                    id="sgp-signature"
                    width="100%"
                    label={texts.SIGNATURE_IMAGE}
                    ContainerProps={{
                        width: '100%',
                        marginTop: '10px',
                        marginBottom: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    selectedFiles={
                        selectedSignatureFile
                            ? [selectedSignatureFile]
                            : form.signatureImage
                            ? [form.signatureImage]
                            : []
                    }
                    handleFileChange={handleSignatureChange}
                    removeFile={() => {
                        handleRemoveSignature();
                        setSelectedSignatureFile(null);
                    }}
                    error={errors.signatureImage}
                />
            </Box>
        </Box>
    );
};

SGPCompanyForm.propTypes = {
    form: PropTypes.shape({
        logoUrl: PropTypes.string,
        signatureImageUrl: PropTypes.string,
        name: PropTypes.string,
        rcsNumber: PropTypes.string,
        rcsRegistrationAddress: PropTypes.string,
        amfData: PropTypes.string,
        legalRepresentativeName: PropTypes.string,
        legalRepresentativeFunction: PropTypes.string,
        contactEmail: PropTypes.string,
        documentsFooterText: PropTypes.string,
        currentAddress: PropTypes.object,
        logo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        signatureImage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        distributorBillEmail: PropTypes.string,
        bankIdentifier: PropTypes.string,
    }).isRequired,
    handleChange: PropTypes.func,
    handleAddressChange: PropTypes.func,
    errors: PropTypes.object,
    handleLogoChange: PropTypes.func,
    handleRemoveLogo: PropTypes.func,
    handleSignatureChange: PropTypes.func,
    handleRemoveSignature: PropTypes.func,
};

export default SGPCompanyForm;
